/**
 * Mine — Proximity-triggered ground mine with LCD countdown.
 *
 * State machine: IDLE → TRIGGERED → EXPLODING → DEAD
 * - IDLE: pulsing sprite on the ground, waiting for enemy proximity
 * - TRIGGERED: 3-2-1 LCD countdown (green crystal digits)
 * - EXPLODING: applies damage + stun + pushback, VFX, then dies
 * - DEAD: cleaned up by MineManager
 *
 * Chain reactions: if pushback sends an enemy into another mine,
 * MineManager.checkChainReaction() queues that mine with a short delay.
 */

import Phaser from 'phaser';
import { MINE_CONFIG } from '@/components/ComponentDefs';
import { Enemy } from '@/entities/Enemy';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager } from '@/systems/SoundManager';
import { JuiceEffects } from '@/systems/JuiceEffects';
import { TILE_SIZE, DECOR_RESOURCE_YIELDS } from '@/config/GameConfig';
import { getDecorAt, damageDecor, removeDecor } from '@/map/MapData';
import { GOD } from '@/config/GodMode';

export const enum MineState {
  IDLE = 0,
  TRIGGERED = 1,
  EXPLODING = 2,
  DEAD = 3,
}

export interface MineStats {
  damage: number;
  triggerRadius: number;
  explosionRadius: number;
  stunDuration: number;
  pushbackTiles: number;
}

/** Callback so Mine can request chain-reaction checks without importing MineManager */
export type ChainReactionCb = (enemy: Enemy) => void;

let nextMineId = 0;

// Mine sprite scale per evolution level (sprite is ~128px, mine on map ~12-14px)
const MINE_SCALE = [0.09, 0.10, 0.11, 0.12];

export class Mine {
  readonly id: string;
  readonly scene: Phaser.Scene;
  readonly x: number;
  readonly y: number;
  readonly tileCol: number;
  readonly tileRow: number;

  alive = true;
  state: MineState = MineState.IDLE;

  // Stats (pre-evolved)
  readonly damage: number;
  readonly triggerRadius: number;
  readonly explosionRadius: number;
  readonly stunDuration: number;
  readonly pushbackTiles: number;
  readonly evolutionLevel: number;

  /** Chain depth (0 = first mine, incremented for chain reactions) */
  chainDepth = 0;

  // Visuals
  private body: Phaser.GameObjects.Image;
  private countdownText: Phaser.GameObjects.Text | null = null;
  private pulseTween: Phaser.Tweens.Tween | null = null;

  // Countdown
  private countdownTimer = 0;
  private countdownDisplay = 0; // current digit shown (3, 2, 1)

  // External callback for chain reactions
  private onChainReaction: ChainReactionCb | null = null;

  constructor(
    scene: Phaser.Scene, x: number, y: number,
    tileCol: number, tileRow: number,
    stats: MineStats, evolutionLevel: number,
    textureKey: string,
  ) {
    this.id = `mine_${nextMineId++}`;
    this.scene = scene;
    this.x = x;
    this.y = y;
    this.tileCol = tileCol;
    this.tileRow = tileRow;
    this.damage = stats.damage;
    this.triggerRadius = stats.triggerRadius;
    this.explosionRadius = stats.explosionRadius;
    this.stunDuration = stats.stunDuration;
    this.pushbackTiles = stats.pushbackTiles;
    this.evolutionLevel = evolutionLevel;

    // Sprite-based mine body
    const finalScale = MINE_SCALE[Math.min(evolutionLevel, MINE_SCALE.length - 1)];
    this.body = scene.add.image(x, y, textureKey)
      .setDepth(15)
      .setScale(0)
      .setAlpha(0);

    // Spawn "pop" animation — mine emerges from ground
    scene.tweens.add({
      targets: this.body,
      scaleX: finalScale,
      scaleY: finalScale,
      alpha: 0.75,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Start idle pulse after spawn animation
        this.pulseTween = scene.tweens.add({
          targets: this.body,
          alpha: { from: 0.6, to: 0.85 },
          duration: 800,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      },
    });
  }

  /** Set chain reaction callback (called by MineManager) */
  setChainCallback(cb: ChainReactionCb): void {
    this.onChainReaction = cb;
  }

  // ── State transitions ──

  trigger(): void {
    if (this.state !== MineState.IDLE) return;
    this.state = MineState.TRIGGERED;
    this.countdownTimer = MINE_CONFIG.countdownSec;
    this.countdownDisplay = MINE_CONFIG.countdownSec;

    // Stop idle pulse
    this.pulseTween?.stop();
    this.pulseTween = null;
    this.body.setAlpha(1);

    // Create LCD countdown text
    this.countdownText = this.scene.add.text(this.x, this.y - 14, `${this.countdownDisplay}`, {
      fontFamily: '"Courier New", monospace',
      fontSize: '14px',
      color: '#00ff41',
      stroke: '#003300',
      strokeThickness: 2,
      shadow: { color: '#00ff41', blur: 6, fill: true, offsetX: 0, offsetY: 0 },
    }).setOrigin(0.5).setDepth(25);

    // Scale punch for first digit
    this.scene.tweens.add({
      targets: this.countdownText,
      scaleX: { from: 1.4, to: 1 },
      scaleY: { from: 1.4, to: 1 },
      duration: 200,
      ease: 'Back.easeOut',
    });

    SoundManager.play('click');
    EventBus.emit(GameEvents.MINE_TRIGGERED, { x: this.x, y: this.y, id: this.id });
  }

  /** Trigger immediately for chain reactions (skip full countdown, short delay handled by MineManager) */
  triggerChain(depth: number): void {
    if (this.state !== MineState.IDLE) return;
    this.chainDepth = depth;
    this.state = MineState.TRIGGERED;
    this.countdownTimer = 0.05; // near-instant
    this.countdownDisplay = 0;

    this.pulseTween?.stop();
    this.pulseTween = null;

    // Flash body red briefly
    this.body.setAlpha(1);
    this.scene.tweens.add({
      targets: this.body,
      alpha: { from: 1, to: 0.5 },
      duration: 50,
      yoyo: true,
    });
  }

  update(dt: number): void {
    if (this.state !== MineState.TRIGGERED) return;

    this.countdownTimer -= dt;

    // Update LCD digit
    const newDigit = Math.ceil(this.countdownTimer);
    if (newDigit !== this.countdownDisplay && newDigit > 0 && this.countdownText) {
      this.countdownDisplay = newDigit;
      this.countdownText.setText(`${newDigit}`);
      // Scale punch on digit change
      this.scene.tweens.add({
        targets: this.countdownText,
        scaleX: { from: 1.4, to: 1 },
        scaleY: { from: 1.4, to: 1 },
        duration: 200,
        ease: 'Back.easeOut',
      });
      SoundManager.play('click');
    }

    if (this.countdownTimer <= 0) {
      this.explode();
    }
  }

  /** Explode: apply damage/stun/pushback to nearby enemies, emit event, VFX */
  explode(): void {
    if (this.state === MineState.EXPLODING || this.state === MineState.DEAD) return;
    this.state = MineState.EXPLODING;

    // VFX — full explosion (flash + debris + shake + particles)
    JuiceEffects.deathBurst(this.scene, this.x, this.y, 0xff8800);
    JuiceEffects.sparkleRing(this.scene, this.x, this.y, 0xff8800, 12 + this.chainDepth * 4);
    JuiceEffects.cameraFlash(this.scene, 0xff6600, 80, 0.08 + this.chainDepth * 0.04);
    JuiceEffects.screenShake(this.scene, 0.004 + this.chainDepth * 0.002, 120);
    SoundManager.play('explosion');

    EventBus.emit(GameEvents.MINE_EXPLODED, {
      x: this.x, y: this.y, chain: this.chainDepth, id: this.id,
    });

    // Damage nearby resources (with weapon resistance)
    this.damageNearbyResources();

    // Damage is applied by MineManager (it has access to enemy spatial hash)
    // This method just handles visuals + state transition

    this.state = MineState.DEAD;
    this.alive = false;
    this.destroyVisuals();
  }

  /** Damage harvestable resources in explosion radius (with weapon resistance) */
  private damageNearbyResources(): void {
    const resist = GOD.resourceWeaponResist;
    const reducedDmg = this.damage * (1 - resist);
    if (reducedDmg <= 0) return;

    const col = Math.floor(this.x / TILE_SIZE);
    const row = Math.floor(this.y / TILE_SIZE);
    const tileRadius = Math.ceil(this.explosionRadius / TILE_SIZE);

    for (let dr = -tileRadius; dr <= tileRadius; dr++) {
      for (let dc = -tileRadius; dc <= tileRadius; dc++) {
        const tc = col + dc;
        const tr = row + dr;
        const decor = getDecorAt(tc, tr);
        if (!decor || !DECOR_RESOURCE_YIELDS[decor.type]) continue;

        const cx = tc * TILE_SIZE + TILE_SIZE / 2;
        const cy = tr * TILE_SIZE + TILE_SIZE / 2;
        const dx = cx - this.x;
        const dy = cy - this.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > this.explosionRadius) continue;

        const falloff = Math.max(0.3, 1 - dist / this.explosionRadius);
        const remaining = damageDecor(decor, reducedDmg * falloff);

        const maxHp = DECOR_RESOURCE_YIELDS[decor.type]!.hp;
        EventBus.emit(GameEvents.DECOR_MINING, { col: tc, row: tr, hp: remaining, maxHp });

        if (remaining <= 0) {
          removeDecor(decor);
          EventBus.emit(GameEvents.DECOR_HARVESTED, {
            col: tc, row: tr, type: decor.type, co: 0, electro: 0,
          });
        }
      }
    }
  }

  /** Get enemies to damage — called by MineManager after explode() */
  applyDamageToEnemies(enemies: Enemy[]): number {
    let kills = 0;
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const dx = enemy.body.x - this.x;
      const dy = enemy.body.y - this.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > this.explosionRadius) continue;

      const wasAlive = enemy.alive;
      const proximity = 1 - dist / this.explosionRadius;
      enemy.takeDamage(this.damage * Math.max(0.3, proximity) * GOD.robotDamageMult);
      enemy.stun(this.stunDuration);
      enemy.knockBack(this.pushbackTiles, proximity);
      if (wasAlive && !enemy.alive) kills++;

      // Chain reaction check via callback
      if (this.onChainReaction) {
        this.onChainReaction(enemy);
      }
    }
    return kills;
  }

  private destroyVisuals(): void {
    this.body.destroy();
    this.countdownText?.destroy();
    this.countdownText = null;
  }

  destroy(): void {
    this.alive = false;
    this.state = MineState.DEAD;
    this.pulseTween?.stop();
    this.pulseTween = null;
    this.destroyVisuals();
  }
}
